// services/PasskeyService.ts
// Browser-side WebAuthn wrapper for passkey operations
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/types';
import { api } from '../utils/api';

export interface Passkey {
  id: number;
  device_type: string | null;
  friendly_name: string | null;
  created_at: string;
  last_used_at: string | null;
  backup_eligible: boolean;
  backup_state: boolean;
}

export class PasskeyService {
  static isAvailable(): boolean {
    return (
      window?.PublicKeyCredential !== undefined &&
      typeof window.PublicKeyCredential === 'function'
    );
  }

  static async isConditionalUIAvailable(): Promise<boolean> {
    if (!PasskeyService.isAvailable()) return false;

    try {
      const pkc = window.PublicKeyCredential as any;
      if (typeof pkc.isConditionalMediationAvailable === 'function') {
        return (await pkc.isConditionalMediationAvailable()) || false;
      }
      return false;
    } catch {
      return false;
    }
  }

  static async registerPasskey(friendlyName?: string): Promise<{ success: boolean; message: string }> {
    try {
      if (!PasskeyService.isAvailable()) {
        return { success: false, message: 'Passkeys are not supported on this device' };
      }

      const optionsResponse = await api.post('/api/passkeys/registration/options', {});
      if (!optionsResponse.success) {
        return { success: false, message: optionsResponse.message || 'Failed to start registration' };
      }

      const options: PublicKeyCredentialCreationOptionsJSON = optionsResponse.options;

      let registrationResponse: RegistrationResponseJSON;
      try {
        registrationResponse = await startRegistration({ optionsJSON: options });
      } catch (error: any) {
        if (error.name === 'NotAllowedError') {
          return { success: false, message: 'Registration was cancelled' };
        }
        return { success: false, message: 'Failed to create passkey' };
      }

      const verifyResponse = await api.post('/api/passkeys/registration/verify', {
        response: registrationResponse,
        friendlyName,
      });

      if (!verifyResponse.success) {
        return { success: false, message: verifyResponse.message || 'Failed to verify passkey' };
      }

      return { success: true, message: 'Passkey registered successfully' };
    } catch (error) {
      console.error('Passkey registration error:', error);
      return { success: false, message: 'An error occurred during registration' };
    }
  }

  static async authenticateWithPasskey(email: string): Promise<{ success: boolean; message: string }> {
    try {
      if (!PasskeyService.isAvailable()) {
        return { success: false, message: 'Passkeys are not supported on this device' };
      }

      const optionsResponse = await api.post('/api/passkeys/authentication/options', { email });
      if (!optionsResponse.success) {
        return { success: false, message: optionsResponse.message || 'Failed to start authentication' };
      }

      const options: PublicKeyCredentialRequestOptionsJSON = optionsResponse.options;

      let authResponse: AuthenticationResponseJSON;
      try {
        authResponse = await startAuthentication({ optionsJSON: options });
      } catch (error: any) {
        if (error.name === 'NotAllowedError') {
          return { success: false, message: 'Authentication was cancelled' };
        }
        return { success: false, message: 'Failed to authenticate with passkey' };
      }

      const verifyResponse = await api.post('/api/passkeys/authentication/verify', {
        email,
        response: authResponse,
      });

      if (!verifyResponse.success) {
        return { success: false, message: verifyResponse.message || 'Authentication failed' };
      }

      return { success: true, message: 'Authentication successful' };
    } catch (error) {
      console.error('Passkey authentication error:', error);
      return { success: false, message: 'An error occurred during authentication' };
    }
  }

  static async startConditionalAuthentication(
    onSuccess: (response: AuthenticationResponseJSON) => void,
    onError: (error: string) => void
  ): Promise<AbortController | null> {
    try {
      if (!PasskeyService.isAvailable()) return null;

      const conditionalUIAvailable = await PasskeyService.isConditionalUIAvailable();
      if (!conditionalUIAvailable) return null;

      const optionsResponse = await api.post('/api/passkeys/authentication/options/conditional', {});
      if (!optionsResponse.success) return null;

      const options: PublicKeyCredentialRequestOptionsJSON = optionsResponse.options;
      const abortController = new AbortController();

      startAuthentication({ optionsJSON: options, useBrowserAutofill: true })
        .then(onSuccess)
        .catch((error) => {
          if (error.name === 'NotAllowedError' || error.name === 'AbortError') return;
          if (error.message?.includes('No <input>')) return;
          onError(error.message || 'Authentication failed');
        });

      return abortController;
    } catch {
      return null;
    }
  }

  static async verifyConditionalAuthentication(
    response: AuthenticationResponseJSON
  ): Promise<{ success: boolean; message: string }> {
    try {
      const verifyResponse = await api.post('/api/passkeys/authentication/verify', { response });
      if (!verifyResponse.success) {
        return { success: false, message: verifyResponse.message || 'Authentication failed' };
      }
      return { success: true, message: 'Authentication successful' };
    } catch {
      return { success: false, message: 'An error occurred during authentication' };
    }
  }

  static async hasPasskeys(email: string): Promise<boolean> {
    try {
      const response = await api.get(`/api/passkeys/check?email=${encodeURIComponent(email)}`);
      return response.success && response.hasPasskeys;
    } catch {
      return false;
    }
  }

  static async listPasskeys(): Promise<Passkey[]> {
    try {
      const response = await api.get('/api/passkeys/list');
      if (!response.success) throw new Error(response.message);
      return response.passkeys;
    } catch {
      return [];
    }
  }

  static async deletePasskey(passkeyId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/api/passkeys/${passkeyId}`, {});
      return { success: response.success, message: response.message || 'Passkey deleted' };
    } catch {
      return { success: false, message: 'An error occurred while deleting the passkey' };
    }
  }

  static async renamePasskey(passkeyId: number, friendlyName: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.patch(`/api/passkeys/${passkeyId}/name`, { friendlyName });
      return { success: response.success, message: response.message || 'Passkey renamed' };
    } catch {
      return { success: false, message: 'An error occurred while renaming the passkey' };
    }
  }
}
